import { useQuery } from 'react-apollo';
import gql from 'graphql-tag';
import { useMemo, useEffect } from 'react';

import { getMonitoringClient } from '@confluence/monitoring';
import { Attribution } from '@confluence/error-boundary';
import { markErrorAsHandled } from '@confluence/graphql';
import { fg } from '@confluence/feature-gating';

import type { ContentTypesHeaderQueryNode } from './ContentTypesHeader';
import type { LastEditedVersion } from './object-header-items/LastEdited';

type UseObjectHeaderVersionType = {
	contentData: ContentTypesHeaderQueryNode | null | undefined;
	useDraftVersion: boolean;
};

const useObjectHeaderVersionDraftQuery = gql`
	query useObjectHeaderVersionDraftQuery($contentId: ID!) {
		content(id: $contentId, status: ["draft"]) {
			nodes {
				version {
					contentTypeModified
					number
					when
					friendlyWhen
					by {
						displayName
					}
				}
			}
		}
	}
`;

const DEFAULT_VERSION: LastEditedVersion = {
	by: {
		fullName: '',
	},
	contentTypeModified: false,
	number: 1,
	when: '',
	friendlyWhen: '',
};

/**
 * Constructs Version data for LastEdited, including draft lastEditedTime as well
 */
export const useObjectHeaderVersion = ({
	contentData,
	useDraftVersion,
}: UseObjectHeaderVersionType): LastEditedVersion => {
	const {
		data: draftDataOverride,
		loading: draftDataLoading,
		error,
	} = useQuery(useObjectHeaderVersionDraftQuery, {
		variables: {
			contentId: contentData?.id,
		},
		fetchPolicy: 'no-cache', // Apollo cache is keyed by content id, not content status, so avoid interfering with cache data
		skip: !useDraftVersion || fg('cc_object_header_draft_version'),
	});

	useEffect(() => {
		if (error) {
			getMonitoringClient().submitError(error, {
				attribution: Attribution.COMMENTS,
			});
			markErrorAsHandled(error);
		}
	}, [error]);

	const version = useMemo<LastEditedVersion>(() => {
		if (useDraftVersion) {
			// use content.draftVersion data
			if (fg('cc_object_header_draft_version')) {
				return {
					contentTypeModified: contentData?.draftVersion?.contentTypeModified ?? false,
					number: contentData?.draftVersion?.number ?? 1,
					when: contentData?.draftVersion?.when ?? '',
					friendlyWhen: contentData?.draftVersion?.friendlyWhen ?? '',
					by: {
						fullName: contentData?.draftVersion?.by?.displayName ?? '',
					},
				};
			}

			if (draftDataLoading) {
				return DEFAULT_VERSION;
			}

			// Use requeried draft version data
			const draftVersion = draftDataOverride?.content?.nodes?.[0]?.version;
			if (draftVersion) {
				return {
					contentTypeModified: draftVersion.contentTypeModified,
					number: draftVersion.number,
					when: draftVersion.when,
					friendlyWhen: draftVersion.friendlyWhen,
					by: {
						fullName: draftVersion.by.displayName,
					},
				};
			}
		}

		// Always prefer the regular content version as a fallback
		return {
			contentTypeModified: contentData?.version?.contentTypeModified ?? false,
			number: contentData?.version?.number ?? 1,
			when: contentData?.version?.when ?? '',
			friendlyWhen: contentData?.version?.friendlyWhen ?? '',
			by: {
				fullName: contentData?.version?.by?.displayName ?? '',
			},
		};
	}, [contentData, useDraftVersion, draftDataOverride, draftDataLoading]);

	return version;
};
